import { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { motion } from "framer-motion";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(undefined);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  let authCheckTimeout;

  useEffect(() => {
    // show spinner while we figure out auth+credits
    setLoading(true);

    let unsubscribeSnapshot = null;

    // 1️⃣ listen for auth changes
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot(); // 🚀 cancel previous snapshot listener!
        unsubscribeSnapshot = null;
      }

      setCurrentUser(user);

      if (!user) {
        // logged out → zero out credits & stop loading
        setCredits(0);
        setLoading(false);
        return;
      }

      // 2️⃣ when logged in, subscribe to their doc in Firestore
      
      // Delay setting snapshot slightly after login to avoid race condition
      authCheckTimeout = setTimeout(() => {
        const userDocRef = doc(db, "users", user.uid);
        unsubscribeSnapshot = onSnapshot(userDocRef, (snap) => {
          if (snap.exists()) {
            setCredits(snap.data().credits || 0);
          } else {
            setCredits(0);
          }
          setLoading(false);
        });
      }, 50);  // ⏲️ 50ms small delay fixes it

    });

    // cleanup _both_ listeners on unmount
    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }, []);

  const value = {
    currentUser,
    credits,
    setCredits, // ✅ expose setCredits so you can update after watching ad, generating image, etc
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col justify-center items-center min-h-screen bg-black/80 backdrop-blur-md"
        >
          <svg
            className="animate-spin h-10 w-10 text-white mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            ></path>
          </svg>

          <div className="text-white text-xl font-bold animate-pulse">
            Loading...
          </div>
        </motion.div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}
